import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2, Eye, EyeOff } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-[#1B2A4A] text-white flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F5961B] flex items-center justify-center">
            <Link2 size={20} className="text-white" />
          </div>
          <div className="font-bold text-xl tracking-wide">BROWARD <span className="text-[#5FA3E8]">LOCAL</span></div>
        </div>
        <div>
          <h1 className="text-4xl font-bold leading-tight tracking-tight">
            Verified local pros,<br />across Broward County.
          </h1>
          <p className="mt-4 text-white/60 text-lg max-w-md">
            Internal data operations console — seed listings, manage the verification pipeline, and prepare the marketplace for launch.
          </p>
        </div>
        <div className="text-white/40 text-sm">Phase 1 · Internal Use Only</div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#F5F7FA] p-6">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-8">
          <h2 className="text-2xl font-bold text-[#1B2A4A] tracking-tight" data-testid="login-heading">Sign in</h2>
          <p className="text-sm text-[#475569] mt-1 mb-6">Admin &amp; employee access</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input data-testid="login-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@browardlocal.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input data-testid="login-password-input" id="password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pr-10" />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1B2A4A] transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-[#2E7BD6] hover:bg-[#2568b8] text-white h-10">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
